import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";

const navLinks = [
  { label: "首页", to: "/" },
  { label: "官方渠道", to: "/#official" },
  { label: "漫画分类", to: "/#categories" },
  { label: "追更指南", to: "/articles" },
  { label: "常见问题", to: "/faq" },
  { label: "关于我们", to: "/about" },
];

interface NavbarProps {
  variant?: "hero" | "solid";
}

export default function Navbar({ variant = "solid" }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  const solid = variant === "solid" || scrolled || !onHome || open;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        solid
          ? "bg-background-50/90 backdrop-blur-md border-b border-background-200"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 md:px-6" aria-label="主导航">
        <Link to="/" className="flex items-center gap-2" aria-label="正版韩漫大全首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-bubble-chart-line text-lg"></i>
          </span>
          <span className={`font-comic text-lg tracking-wide ${solid ? "text-foreground-900" : "text-background-50"}`}>
            正版韩漫大全
          </span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.to}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                solid
                  ? "text-foreground-700 hover:bg-primary-50 hover:text-primary-600"
                  : "text-background-100 hover:bg-background-50/10 hover:text-background-50"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            className="ml-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            联系我们
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`flex h-10 w-10 items-center justify-center rounded-md md:hidden ${
            solid ? "text-foreground-800" : "text-background-50"
          }`}
          aria-label={open ? "关闭菜单" : "打开菜单"}
          aria-expanded={open}
        >
          <i className={`text-xl ${open ? "ri-close-line" : "ri-menu-line"}`}></i>
        </button>
      </nav>

      {open && (
        <div className="border-t border-background-200 bg-background-50/95 px-4 py-3 backdrop-blur-md md:hidden">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="rounded-md px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-600"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="mt-1 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-center text-sm font-medium text-background-50"
            >
              联系我们
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}